import { useMemo, useState } from 'react';
import { Modal } from 'antd';
import { Actions, CallDrawer, DangerLink, DetailModal, Field, SearchSelect, Select } from '../../components';
import { useAuth } from '../../context/AuthContext';
import { useBookingAssigneesQuery, shortName } from '../../queries/scheduling';
import { useDeleteProspect, useUpdateProspect } from '../../queries/prospects';
import { useTenants } from '../../queries/clients';
import { STAGES, agentOf, stageFull } from '../../utils/prospects';
import ScoreBreakdown from './ScoreBreakdown';
import TourPicker from './TourPicker';
import ConvertBlock from './ConvertBlock';
import * as S from './styled';

const STAGE_SIGNALS = {
  'Tour Scheduled': 'tourScheduled',
  'Application Submitted': 'appSubmitted',
  'Background Check Submitted': 'bgSubmitted',
  'Contract Signed': 'contract'
};
const TENANT_PAGE = { page: 1, pageSize: 100 };
const CONTACT_FIELDS = [
  { key: 'name', label: 'Name', required: true },
  { key: 'phone', label: 'Phone', type: 'tel' },
  { key: 'email', label: 'Email', type: 'email' },
  { key: 'interestedProperty', label: 'Property' }
];

function ContactField({ prospect, field, canManage, onSave }) {
  const value = prospect[field.key] || '';
  const id = `prospect-${field.key}`;
  return (
    <div>
      <dt>{canManage ? <label htmlFor={id}>{field.label}</label> : field.label}</dt>
      <dd>
        {canManage ? (
          <Field
            key={value}
            id={id}
            type={field.type || 'text'}
            defaultValue={value}
            onBlur={(e) => {
              const next = e.target.value.trim();
              if (next !== value && (next || !field.required)) onSave({ [field.key]: next });
            }}
          />
        ) : (
          value || '—'
        )}
      </dd>
    </div>
  );
}

export default function ProspectModal({ prospect, rows, canManage, onClose }) {
  const { data: assignees = [] } = useBookingAssigneesQuery();
  const { rows: tenants } = useTenants(TENANT_PAGE);
  const update = useUpdateProspect();
  const remove = useDeleteProspect();
  const { can } = useAuth();
  const [callId, setCallId] = useState(null);
  const agentOptions = useMemo(() => {
    const names = new Set(
      [...rows, ...tenants].map((r) => r.assignedTo).concat(assignees.map(shortName), prospect.assignedTo).filter(Boolean)
    );
    names.delete('Unassigned');
    return ['Unassigned', ...[...names].sort()];
  }, [rows, tenants, assignees, prospect.assignedTo]);

  const patch = (changes) => update.mutate({ id: prospect._id, ...changes });
  const confirmDelete = () =>
    Modal.confirm({
      title: `Delete ${prospect.name}?`,
      content: 'The prospect is removed from the pipeline and any booked tour is cancelled.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        onClose();
        remove.mutate(prospect._id);
      }
    });
  const setStage = (pipelineStage) =>
    patch({ pipelineStage, ...(STAGE_SIGNALS[pipelineStage] && { signals: { [STAGE_SIGNALS[pipelineStage]]: true } }) });

  return (
    <DetailModal
      width={620}
      top={36}
      eyebrow={stageFull(prospect)}
      title={prospect.name}
      subtitle={[prospect.interestedProperty, prospect.source].filter(Boolean).join(' · ')}
      onClose={onClose}
      footer={
        canManage ? (
          <Actions>
            <DangerLink type="button" onClick={confirmDelete}>
              Delete prospect
            </DangerLink>
          </Actions>
        ) : null
      }
    >
      <S.ModalBody>
        <ScoreBreakdown prospect={prospect} onToggle={canManage ? (key, on) => patch({ signals: { [key]: on } }) : undefined} />
        <TourPicker prospect={prospect} canManage={canManage} />
        <S.FactGrid>
          {CONTACT_FIELDS.map((f) => (
            <ContactField key={f.key} prospect={prospect} field={f} canManage={canManage} onSave={patch} />
          ))}
          <div>
            <dt>Source</dt>
            <dd>
              {prospect.source || '—'}
              {prospect.callRecordId && can('view_calls') && (
                <>
                  {' · '}
                  <S.LinkBtn type="button" onClick={() => setCallId(prospect.callRecordId)}>
                    Open call
                  </S.LinkBtn>
                </>
              )}
            </dd>
          </div>
          <div>
            <dt>{canManage ? <label htmlFor="prospect-agent">Assigned agent</label> : 'Assigned agent'}</dt>
            <dd>
              {canManage ? (
                <SearchSelect
                  id="prospect-agent"
                  value={agentOf(prospect)}
                  onChange={(v) => patch({ assignedTo: v === 'Unassigned' ? '' : v })}
                  options={agentOptions.map((a) => ({ value: a, label: a }))}
                />
              ) : (
                agentOf(prospect)
              )}
            </dd>
          </div>
          <div>
            <dt>{canManage ? <label htmlFor="prospect-stage">Stage</label> : 'Stage'}</dt>
            <dd>
              {canManage ? (
                <Select id="prospect-stage" value={prospect.pipelineStage} onChange={(e) => setStage(e.target.value)}>
                  {STAGES.map((s) => (
                    <option key={s.key}>{s.key}</option>
                  ))}
                </Select>
              ) : (
                stageFull(prospect)
              )}
            </dd>
          </div>
        </S.FactGrid>
        <ConvertBlock prospect={prospect} agentOptions={agentOptions} canManage={canManage} />
      </S.ModalBody>
      <CallDrawer callId={callId} onClose={() => setCallId(null)} />
    </DetailModal>
  );
}
